/**
 * Route geometry for the map — fetched from trips-server, drawn here (SEC-1).
 *
 * This used to call the JS SDK's DirectionsService, which meant the browser key
 * needed the Directions API enabled. The server asks Google instead and returns
 * Google's ENCODED polyline; decoding it needs only the Maps `geometry` library,
 * which is part of the Maps JavaScript API the map already loads. So the key in
 * the bundle can now be restricted to Maps JS alone.
 */

import { fetchPathFromServer } from '../../../services/places/placesApi';

export type MapTravelMode = 'drive' | 'walk' | 'transit';

export const normalizeMapTravelMode = (mode?: string | null): MapTravelMode => {
  if (mode === 'walk' || mode === 'transit') return mode;
  return 'drive';
};

export const getLegTravelMode = (
  leg?: { mode?: string; chosenMode?: string } | null,
  fallback: MapTravelMode = 'drive',
): MapTravelMode => normalizeMapTravelMode(leg?.chosenMode || leg?.mode || fallback);

// Session cache: the overview path for a (from, to, mode) triple is stable enough
// for display, so route it once and reuse across every re-plot instead of calling
// DirectionsService on each render. Caches the promise (dedupes concurrent
// callers); a null result is evicted so a later attempt can retry.
const directionsCache = new Map<string, Promise<Array<{ lat: number; lng: number }> | null>>();
const coordKey = (p: { lat: number; lng: number }) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;

export const fetchDirectionsPath = (
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
  mode: MapTravelMode,
): Promise<Array<{ lat: number; lng: number }> | null> => {
  const cacheKey = `${coordKey(origin)}|${coordKey(destination)}|${mode}`;
  const cached = directionsCache.get(cacheKey);
  if (cached) return cached;

  const pending = (async (): Promise<Array<{ lat: number; lng: number }> | null> => {
    const polyline = await fetchPathFromServer(origin, destination, mode);
    if (!polyline) return null;

    // No geometry library (map still loading, or a blocked script) is the same
    // answer as no route: the caller draws the straight connector.
    const decode = window.google?.maps?.geometry?.encoding?.decodePath;
    if (!decode) return null;

    try {
      return decode(polyline).map((point: { lat: () => number; lng: () => number }) => ({
        lat: point.lat(),
        lng: point.lng(),
      }));
    } catch {
      return null;
    }
  })();

  directionsCache.set(cacheKey, pending);
  pending.then((res) => { if (!res) directionsCache.delete(cacheKey); }).catch(() => directionsCache.delete(cacheKey));
  return pending;
};
